import { useEffect, useState } from 'react';

const DURATION_MS = 900;

const ease = (progress: number): number =>
  progress < 0.5 ? 2 * progress ** 2 : 1 - (-2 * progress + 2) ** 2 / 2;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useCountUp = (target: number | null): number | null => {
  // Starts at the real target, not 0. Effects never run during SSR/no-JS, so
  // starting at 0 would leave the server-rendered/no-JS markup permanently
  // showing $0 for every real metric - worse than the alternative. The
  // tradeoff this accepts: JS users with SSR/cache-warmed data see the final
  // value painted first, then a reset to 0 before the count-up plays. There's
  // no initial value that avoids both failure modes for a 0-to-target
  // animation on data already known at render time - this picks correctness
  // for no-JS/hydration-failure over a fully flash-free animation.
  const [value, setValue] = useState<number | null>(target);

  useEffect(() => {
    if (target === null || prefersReducedMotion()) {
      setValue(target);
      return undefined;
    }

    setValue(0);
    let frame: number;
    let start: number | null = null;

    const tick = (timestamp: number): void => {
      if (start === null) {
        start = timestamp;
      }
      const progress = Math.min((timestamp - start) / DURATION_MS, 1);
      setValue(target * ease(progress));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return value;
};

export default useCountUp;
